import React, { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import PropTypes from "prop-types"

import styles from "./TwoColumnSlider.module.scss"

const TwoColumnSlider = ({
  slides,
  autoplay = false,
  autoplayInterval = 3000,
  pauseOnInteractionDuration,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const autoplayRef = useRef()
  const pauseTimeoutRef = useRef(null)
  const intervalRef = useRef(null)
  const isPausedRef = useRef(false)
  const { t } = useTranslation()

  const pauseAutoplayTemporarily = ms => {
    if (!ms) return
    isPausedRef.current = true
    clearTimeout(pauseTimeoutRef.current)
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false
    }, ms)
  }

  useEffect(() => {
    if (!autoplay) return

    autoplayRef.current = () => {
      if (!isPausedRef.current) {
        setActiveIndex(prev => (prev + 1) % slides.length)
      }
    }

    intervalRef.current = setInterval(() => {
      autoplayRef.current()
    }, autoplayInterval)

    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(pauseTimeoutRef.current)
    }
  }, [autoplay, autoplayInterval, slides.length])

  const handleClick = index => {
    setActiveIndex(index)
    pauseAutoplayTemporarily(pauseOnInteractionDuration)
  }

  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => handleClick(index)}
            className={`${styles.button} ${
              index === activeIndex ? styles.buttonActive : ""
            }`}
            aria-pressed={index === activeIndex}
          >
            <strong className={styles.title}>{t(slide.title)}</strong>
            <small className={styles.description}>{t(slide.description)}</small>
          </button>
        ))}
      </div>

      <div className={styles.rightColumn}>
        <img
          src={slides[activeIndex].imageSrc}
          alt={t(slides[activeIndex].imageAlt || slides[activeIndex].title)}
          className={styles.image}
        />
      </div>
    </div>
  )
}

TwoColumnSlider.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      imageSrc: PropTypes.string.isRequired,
      imageAlt: PropTypes.string,
    })
  ).isRequired,
  autoplay: PropTypes.bool,
  autoplayInterval: PropTypes.number,
}

export default TwoColumnSlider
